// WbProductsPage — /weighbridge/products (admin only).
//
// Phase C3 Group 4. Lists the tenant's products from /api/v1/wb/products.
// Server-side paginated and filterable by name (LIKE), mode, direction,
// and stock_tracked. Row click navigates to the detail/edit page; the
// "+ Add Product" button routes to /new.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Package } from 'lucide-react';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Badge } from '../../../components/ui/Badge/Badge';
import { EmptyState } from '../../../components/ui/EmptyState/EmptyState';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import {
  useWbProducts,
  type DirectionDefault,
  type ProductMode,
  type UnitOfMeasure,
  type WbProduct,
} from '../hooks/useWbProducts';
import { useWbProductGroups } from '../hooks/useWbProductGroups';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbProductsPage.module.css';

const PER_PAGE = 50;

const MODE_LABEL: Record<ProductMode, string> = {
  weight: 'Weight',
  unit: 'Unit',
};

const UNIT_LABEL: Record<UnitOfMeasure, string> = {
  tonne: 'tonne',
  kg: 'kg',
  each: 'each',
  m3: 'm³',
};

const DIRECTION_LABEL: Record<DirectionDefault, string> = {
  inwards: 'Inwards',
  outwards: 'Outwards',
  both: 'Both',
};

// One shared instance — avoids reconstructing the formatter on every row.
const GBP = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
});

export function WbProductsPage(): JSX.Element {
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [modeFilter, setModeFilter] = useState<'' | ProductMode>('');
  const [directionFilter, setDirectionFilter] = useState<'' | DirectionDefault>(
    '',
  );
  const [stockTracked, setStockTracked] = useState(false);
  const [groupFilter, setGroupFilter] = useState(''); // group code, '' = all
  const [page, setPage] = useState(1);

  const groups = useWbProductGroups().data?.groups ?? [];
  // Resolve the chosen group code to its enc_id from the SAME list response, so
  // the filter param matches the dropdown (enc_ids rotate across responses).
  const groupFilterEncId =
    groupFilter === ''
      ? undefined
      : groups.find((g) => g.code === groupFilter)?.enc_id;

  // 300ms debounce on the search input; reset to page 1 on every change.
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 when any filter changes so we don't end up on page 5
  // of a freshly narrowed set.
  useEffect(() => {
    setPage(1);
  }, [modeFilter, directionFilter, stockTracked, groupFilter]);

  const params = useMemo(
    () => ({
      page,
      per_page: PER_PAGE,
      ...(debouncedSearch ? { name: debouncedSearch } : {}),
      ...(modeFilter ? { mode: modeFilter } : {}),
      ...(directionFilter ? { direction_default: directionFilter } : {}),
      ...(stockTracked ? { stock_tracked: true } : {}),
      ...(groupFilterEncId ? { group_id: groupFilterEncId } : {}),
    }),
    [page, debouncedSearch, modeFilter, directionFilter, stockTracked, groupFilterEncId],
  );

  const productsQuery = useWbProducts(params);
  const products = productsQuery.data?.products ?? [];
  const pagination = productsQuery.data?.pagination;
  const totalPages = pagination?.total_pages ?? 1;
  const hasAnyFilter =
    debouncedSearch !== '' ||
    modeFilter !== '' ||
    directionFilter !== '' ||
    stockTracked ||
    groupFilter !== '';

  return (
    <div className={styles.page}>
      <PageHeader
        title="Products"
        subtitle="Sellable / weighable items recorded against weighbridge tickets."
        actions={
          <Button
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/weighbridge/products/new')}
          >
            Add Product
          </Button>
        }
      />

      <div className={styles.toolbar}>
        <input
          className={styles.search}
          type="search"
          placeholder="Search by name…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          aria-label="Search products by name"
        />
        <select
          className={styles.filterSelect}
          value={modeFilter}
          onChange={(e) => setModeFilter(e.target.value as '' | ProductMode)}
          aria-label="Filter by mode"
        >
          <option value="">All modes</option>
          <option value="weight">Weight</option>
          <option value="unit">Unit</option>
        </select>
        <select
          className={styles.filterSelect}
          value={directionFilter}
          onChange={(e) =>
            setDirectionFilter(e.target.value as '' | DirectionDefault)
          }
          aria-label="Filter by direction"
        >
          <option value="">All directions</option>
          <option value="inwards">Inwards</option>
          <option value="outwards">Outwards</option>
          <option value="both">Both</option>
        </select>
        <select
          className={styles.filterSelect}
          value={groupFilter}
          onChange={(e) => setGroupFilter(e.target.value)}
          aria-label="Filter by group"
        >
          <option value="">All groups</option>
          {groups
            .filter((g) => !g.is_excluded)
            .map((g) => (
              <option key={g.code} value={g.code}>
                {g.name}
              </option>
            ))}
        </select>
        <label className={styles.filterCheckbox}>
          <input
            type="checkbox"
            checked={stockTracked}
            onChange={(e) => setStockTracked(e.target.checked)}
          />
          <span>Stock-tracked only</span>
        </label>
      </div>

      {productsQuery.isError ? (
        <p className={styles.error}>
          {extractErrorMessage(productsQuery.error)}
        </p>
      ) : null}

      <Card flush>
        {productsQuery.isLoading ? (
          <div className={styles.loadingRow}>
            <Spinner label="Loading products" />
          </div>
        ) : products.length === 0 ? (
          hasAnyFilter ? (
            <EmptyState
              icon={Package}
              title="No matching products"
              description="No products match the current filters. Try clearing them."
            />
          ) : (
            <EmptyState
              icon={Package}
              title="No products yet"
              description="Add a product to start recording it on weighbridge tickets."
              action={
                <Button
                  variant="primary"
                  icon={Plus}
                  onClick={() => navigate('/weighbridge/products/new')}
                >
                  Add Product
                </Button>
              }
            />
          )
        ) : (
          <>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Group</th>
                  <th>Mode</th>
                  <th>Unit</th>
                  <th className={styles.priceCell}>Price</th>
                  <th>VAT</th>
                  <th>EWC</th>
                  <th>Hazardous</th>
                  <th>Direction</th>
                  <th>Active</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {products.map((p: WbProduct) => (
                  <tr
                    key={p.enc_id}
                    onClick={() =>
                      navigate(
                        `/weighbridge/products/${encodeURIComponent(p.enc_id)}`,
                      )
                    }
                  >
                    <td>{p.name}</td>
                    <td className={styles.codeCell}>{p.code}</td>
                    <td>
                      {p.group ? p.group.name : <span className={styles.muted}>—</span>}
                    </td>
                    <td>{MODE_LABEL[p.mode]}</td>
                    <td>{UNIT_LABEL[p.unit_of_measure]}</td>
                    <td className={styles.priceCell}>
                      {GBP.format(p.default_general_public_price)}
                    </td>
                    <td>
                      {p.vat_rate ? (
                        <>
                          <span>{p.vat_rate.name}</span>
                          <span className={styles.vatPercent}>
                            {p.vat_rate.percentage}%
                          </span>
                        </>
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>
                      {p.ewc_code ?? <span className={styles.muted}>—</span>}
                    </td>
                    <td>
                      {p.is_hazardous ? (
                        <Badge variant="danger">Hazardous</Badge>
                      ) : (
                        <span className={styles.muted}>—</span>
                      )}
                    </td>
                    <td>{DIRECTION_LABEL[p.direction_default]}</td>
                    <td>
                      {p.is_active ? (
                        <Badge variant="success">Active</Badge>
                      ) : (
                        <Badge variant="muted">Inactive</Badge>
                      )}
                    </td>
                    <td className={styles.actionsCell}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(
                            `/weighbridge/products/${encodeURIComponent(p.enc_id)}`,
                          );
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {pagination && totalPages > 1 ? (
              <div className={styles.pagination}>
                <span>
                  Page {pagination.page} of {totalPages} ({pagination.total} total)
                </span>
                <div className={styles.pageControls}>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() =>
                      setPage((p) => Math.min(totalPages, p + 1))
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}
